import {Link, useLocation, useSearchParams, useNavigate} from 'react-router';
import {useForm} from 'react-hook-form';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {Button} from '@ui/buttons/button';
import {Form} from '@ui/forms/form';
import {LoginPayload, useLogin} from '../requests/use-login';
import {FormCheckbox} from '@ui/forms/toggle/checkbox';
import {LinkStyle} from '@ui/buttons/external-link';
import {SocialAuthSection} from './social-auth-section';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '@ui/i18n/trans';
import {StaticPageTitle} from '../../seo/static-page-title';
import {useContext} from 'react';
import {
  SiteConfigContext,
  SiteConfigContextValue,
} from '../../core/settings/site-config-context';
import {useSettings} from '@ui/settings/use-settings';
import {MailIcon} from '@ui/icons/material/Mail';

interface Props {
  onTwoFactorChallenge: () => void;
}
export function LoginPage({onTwoFactorChallenge}: Props) {
  const [searchParams] = useSearchParams();
  const {pathname, search} = useLocation();
  const navigate = useNavigate();

  const isWorkspaceLogin = pathname.includes('workspace');
  const searchParamsEmail = searchParams.get('email') || undefined;

  const {branding, registration, site, social} = useSettings();
  const siteConfig = useContext(SiteConfigContext);

  const demoDefaults =
    site.demo && !searchParamsEmail ? getDemoFormDefaults(siteConfig) : {};
  const form = useForm<LoginPayload>({
    defaultValues: {remember: true, email: searchParamsEmail, ...demoDefaults},
  });
  const login = useLogin(form);

  const splashVideo = searchParams.get('splash_video');
  const redirectParam = searchParams.get('redirect');
  const registerUrlParams = new URLSearchParams();
  if (splashVideo) registerUrlParams.set('splash_video', splashVideo);
  if (redirectParam) registerUrlParams.set('redirect', redirectParam);
  const registerQuery = registerUrlParams.toString();

  const heading = isWorkspaceLogin ? (
    <Trans
      values={{siteName: branding?.site_name}}
      message="To join your team on :siteName, login to your account"
    />
  ) : undefined;

  const message = undefined;

  const isInvalid = !!Object.keys(form.formState.errors).length;

  // Keep auth links working under subpath deployments (e.g. /channel/bollymix/login)
  const authBase = pathname.replace(/\/login\/?$/, '');
  const forgotPasswordTo = `${authBase || ''}/forgot-password${search || ''}`;
  const registerTo = registerQuery
    ? `${authBase || ''}/register?${registerQuery}`
    : `${authBase || ''}/register`;

  return (
    <AuthLayout
      heading={
        isWorkspaceLogin ? (
          heading
        ) : (
          <div className="mb-20 flex w-full items-center justify-between border-b border-white/20 pb-12">
            <span className="text-xl font-bold text-white">
              <Trans message="LOGIN" />
            </span>
            <Link className="text-xl text-primary-light hover:underline" to={registerTo}>
              <Trans message="SIGNUP" />
            </Link>
          </div>
        )
      }
      message={message}
    >
      <StaticPageTitle>
        <Trans message="Login" />
      </StaticPageTitle>
      <Form
        form={form}
        onSubmit={payload => {
          login.mutate(payload, {
            onSuccess: response => {
              if (response.two_factor) {
                onTwoFactorChallenge();
              } else {
                const redirect = searchParams.get('redirect') || '/';
                navigate(redirect, { replace: true });
              }
            },
          });
        }}
      >
        <FormTextField
          className=""
          name="email"
          type="email"
          placeholder="Enter email"
          disabled={!!searchParamsEmail}
          invalid={isInvalid}
          required
        />
        <FormTextField
          className="mb-8"
          name="password"
          type="password"
          placeholder="Enter password"
          invalid={isInvalid}
          required
        />
        <div className="mb-20 flex items-center justify-between gap-12">
          <FormCheckbox name="remember" className="block">
            <Trans message="Stay logged in" />
          </FormCheckbox>
          <Link className={LinkStyle} to={forgotPasswordTo} tabIndex={-1}>
            <Trans message="Forgot password?" />
          </Link>
        </div>
        <Button
          className="block w-full"
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          disabled={login.isPending}
          startIcon={<MailIcon />}
        >
          <Trans message="Login with Email" />
        </Button>
      </Form>
      <SocialAuthSection
        dividerMessage={
          social?.compact_buttons ? (
            <Trans message="Or sign in with" />
          ) : (
            <Trans message="OR" />
          )
        }
      />
    </AuthLayout>
  );
}

function getDemoFormDefaults(siteConfig: SiteConfigContextValue) {
  if (siteConfig.demo.loginPageDefaults === 'randomAccount') {
    // random number between 0 and 100, padded to 3 digits
    const number = Math.floor(Math.random() * 100) + 1;
    const paddedNumber = String(number).padStart(3, '0');
    return {
      email: `admin@demo${paddedNumber}.com`,
      password: 'admin',
    };
  } else {
    return {
      email: siteConfig.demo.email ?? 'admin@admin.com',
      password: siteConfig.demo.password ?? 'admin',
    };
  }
}
